<template>
  <div>
    <div v-if="!isOnline" class="network-status" :style="networkStatusStyle">
      <span>Offline</span>
      <span v-if="remainingText !== 'Limit reached'">• Time remaining: {{ remainingText }}</span>
      <span v-else>• Offline sales disabled. Please reconnect to continue.</span>
    </div>
    <div v-if="false" class="support">
      <font-awesome-icon icon="headset" class="support-icon" />
      <span> Please note that our current support line is currently down. Please reach us on 0765631486.</span>
      <button @click="closeSupportNotice" class="close-button">
        <font-awesome-icon icon="times" />
      </button>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faDownload, faSpinner, faHeadset, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapState, mapMutations } from 'vuex';
import { Storage } from '@/services/Storage';
import connectivityMonitor from '@/services/connectivityMonitor';

library.add(faDownload, faSpinner, faHeadset, faTimes);

export default {
  components: {
    FontAwesomeIcon
  },
  computed: {
    ...mapState(['showSupportNotice', 'systemSettings']),
    isPosView() {
      return this.$route.name === 'POSView';
    },
    maxOfflineMs() {
      const hours = (this.systemSettings && (this.systemSettings.maxOfflineHours || this.systemSettings.MaxOfflineHours)) || 3;
      return Number(hours) * 60 * 60 * 1000;
    },
    networkStatusStyle() {
      // Use brand colors and ensure AA contrast
      return {
        background: 'linear-gradient(to right, #339CFF, #A529FF)',
        color: '#FFFFFF',
        padding: '8px 12px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.12)'
      };
    }
  },
  data() {
    return {
      isOnline: connectivityMonitor.getStatus(),
      networkCheckInterval: null,
      intervalId: null,
      unsubTick: null,
      unsubOnline: null,
      unsubOffline: null,
      remainingText: '',
    };
  },
  methods: {
    ...mapMutations(['setShowSupportNotice']),
    closeSupportNotice() {
      this.setShowSupportNotice(false);
      localStorage.setItem('lastSupportNoticeTime', Date.now());
    },
    _updateRemaining() {
      const hours = (this.systemSettings && (this.systemSettings.maxOfflineHours || this.systemSettings.MaxOfflineHours)) || 3;
      const ms = Number(hours) * 60 * 60 * 1000;
      this.remainingText = connectivityMonitor.getRemainingOfflineTimeFormatted(ms);
    },
    startSupportNoticeInterval() {
      const lastTriggerTime = localStorage.getItem('lastSupportNoticeTime');
      const oneHour = 3600000; // 1 hour in ms
      const timeSinceLastTrigger = Date.now() - (lastTriggerTime || 0);

      if (timeSinceLastTrigger < oneHour) {
        const remainingTime = oneHour - timeSinceLastTrigger;
        setTimeout(() => {
          this.setShowSupportNotice(true);
          this.intervalId = setInterval(() => {
            this.setShowSupportNotice(true);
          }, oneHour);
        }, remainingTime);
      } else {

        this.setShowSupportNotice(true);
        this.intervalId = setInterval(() => {
          this.setShowSupportNotice(true);
        }, oneHour);
      }
    },
    async updateNetworkStatus() {
      // Source of truth is backend reachability managed by connectivityMonitor.
      this.isOnline = connectivityMonitor.getStatus();

      // When browser reports online, trigger a fresh backend reachability check
      // so the banner can clear as soon as connectivity is restored.
      if (navigator.onLine && !this.isOnline && connectivityMonitor.forceCheck) {
        try {
          this.isOnline = await connectivityMonitor.forceCheck();
        } catch (e) {
          this.isOnline = connectivityMonitor.getStatus();
        }
      }

      // Update remaining time immediately on any network status change
      this._updateRemaining();
      if (this.isOnline) {
        try {
          // On coming online, if there is a pending float for the current user, prompt them
          const user = this.$store && this.$store.getters && this.$store.getters.getCurrentUser;
          const username = user && user.Username ? user.Username : '';
          if (!username) return;
          const pending = await Storage.getFloatPending(username);
          if (!pending) return;

          // Verify with server if float is already assigned
          const assigned = await this.$store.dispatch('getCurrentUserFloat').catch(() => false);
          if (assigned) {
            await Storage.clearFloatPending(username);
            return;
          }

          // If not assigned and not already on float route, navigate to float
          if (this.$route.name !== 'float') {
            this.$router.push({ name: 'float' });
          }
        } catch (e) {
          // Ignore errors silently
        }
      }
    },
    detectOS() {
      let platform = navigator.platform.toLowerCase();
      let userAgent = navigator.userAgent.toLowerCase();
      if (platform.includes('win')) {
        return 'Windows';
      } else if (/android/.test(userAgent)) {
        return 'Android';
      } else if (platform.includes('mac') || userAgent.includes('mac os')) {
        return 'MacOS';
      } else if (platform.includes('linux')) {
        return 'Linux';
      } else if (/iphone|ipad|ipod/.test(userAgent)) {
        return 'iOS';
      } else {
        return 'Unknown OS';
      }
    },

  },
  created() {
    this.$store.dispatch('initializeStore');
    window.addEventListener('online', this.updateNetworkStatus);
    window.addEventListener('offline', this.updateNetworkStatus);
    this.startSupportNoticeInterval();

    // Ensure initial banner state matches monitor state.
    this.isOnline = connectivityMonitor.getStatus();
    this.updateNetworkStatus();

    // Initialize remaining time and subscribe to connectivity ticks
    this._updateRemaining();
    this.unsubTick = connectivityMonitor.subscribe('tick', () => {
      this._updateRemaining();
    });
    this.unsubOnline = connectivityMonitor.subscribe('online', () => {
      this.isOnline = true;
      this._updateRemaining();
    });
    this.unsubOffline = connectivityMonitor.subscribe('offline', () => {
      this.isOnline = false;
      this._updateRemaining();
    });
  },
  beforeDestroy() {
    window.removeEventListener('online', this.updateNetworkStatus);
    window.removeEventListener('offline', this.updateNetworkStatus);
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.unsubTick) this.unsubTick();
    if (this.unsubOnline) this.unsubOnline();
    if (this.unsubOffline) this.unsubOffline();
  }
};
</script>

<style scoped>
.network-status {
  background-color: #ffc107;
  color: #212529;
  padding: 8px 12px; /* Adjusted padding for smaller banner */
  text-align: center;
  width: auto; /* Allow content to define width */
  max-width: 220px; /* Max width for the banner */
  position: fixed;
  top: 10px; /* Position from top */
  right: 10px; /* Position from right */
  z-index: 999;
  animation: blink 1s infinite;
  border-radius: 6px; /* Slightly rounded corners */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); /* Subtle shadow */
  font-size: 0.85em; /* Slightly smaller font */
  line-height: 1.3; /* Improve readability */
}

.support {
  background-color: rgba(185, 174, 12, 0.712);
  color: #000000;
  padding: 12px 24px;
  text-align: center;
  position: fixed;
  top: 10px;
  right: 600px;
  z-index: 999;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-weight: 500;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 400px;
  font-size: 0.95rem;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: blink 1s infinite;
}

.support-icon {
  font-size: 18px;
  color: #1a73e8;
}

.close-button {
  background: none;
  border: none;
  color: #000000;
  cursor: pointer;
  font-size: 14px;
  margin-left: auto;
}

.close-button:hover {
  opacity: 0.7;
}

.update-banner {
  background-color: #1a73e8;
  color: white;
  padding: 12px 24px;
  width: 100%;
  position: fixed;
  top: 80px; /* This will be overridden by inline style */
  z-index: 999;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.update-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.update-icon {
  font-size: 24px;
  color: #ffffff;
}

.update-text {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.update-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
}

.update-description {
  font-size: 14px;
  opacity: 0.9;
}

.update-button {
  background-color: #ffffff;
  color: #1a73e8;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.update-button:hover:not(:disabled) {
  background-color: #f8f9fa;
  transform: translateY(-1px);
}

.update-button:active:not(:disabled) {
  transform: translateY(0);
}

.update-button.updating {
  background-color: #e8f0fe;
  cursor: not-allowed;
}

.update-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@keyframes blink {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}
</style>
